import React, { useEffect, useState } from 'react';
import { Modal, Form, Select, DatePicker, Descriptions, Button, App, Divider } from 'antd';
import dayjs from 'dayjs';
import cfoService from '../../Services/cfoService';
import cioService from '../../Services/cioService';
import { money, toApiDate } from '../../Utils/format';
import { toApiError } from '../../api/axiosClient';

/**
 * CFO invoice creation (handoff Flow D): pick the customer, PREVIEW the
 * calculated lines/total (no record created), then CREATE the Draft invoice
 * with an issue and due date.
 */
const CreateInvoiceModal = ({ open, onClose, presetClientId, onCreated }) => {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [customers, setCustomers] = useState([]);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPreview(null);
    form.resetFields();
    form.setFieldsValue({
      clientId: presetClientId || undefined,
      issueDate: dayjs(),
      dueDate: dayjs().add(7, 'day'),
    });
    if (!presetClientId) {
      cioService
        .getClients({ pageSize: 100 })
        .then((res) => setCustomers(res?.items || []))
        .catch(() => setCustomers([]));
    }
  }, [open, presetClientId, form]);

  const targetClientId = () => presetClientId || form.getFieldValue('clientId');

  const runPreview = async () => {
    const target = targetClientId();
    if (!target) {
      form.validateFields(['clientId']).catch(() => {});
      return;
    }
    setPreviewing(true);
    try {
      setPreview(await cfoService.previewInvoice(target));
    } catch (err) {
      message.error(toApiError(err).message);
      setPreview(null);
    } finally {
      setPreviewing(false);
    }
  };

  const onCreate = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return; // invalid — field errors render inline
    }
    const target = presetClientId || values.clientId;
    setCreating(true);
    try {
      const invoice = await cfoService.createInvoice({
        clientId: target,
        issueDate: toApiDate(values.issueDate),
        dueDate: toApiDate(values.dueDate),
      });
      message.success('Draft invoice created.');
      onCreated?.(invoice);
      onClose();
    } catch (err) {
      message.error(toApiError(err).message);
    } finally {
      setCreating(false);
    }
  };

  return (
    <Modal
      title="Create Invoice"
      open={open}
      onCancel={onClose}
      destroyOnHidden
      footer={[
        <Button key="cancel" onClick={onClose}>Cancel</Button>,
        <Button key="preview" onClick={runPreview} loading={previewing}>Preview</Button>,
        <Button key="create" type="primary" onClick={onCreate} loading={creating} disabled={!preview}>
          Create Draft
        </Button>,
      ]}
    >
      <Form form={form} layout="vertical" requiredMark="optional">
        {!presetClientId && (
          <Form.Item name="clientId" label="Customer" rules={[{ required: true, message: 'Select a customer' }]}>
            <Select
              showSearch
              optionFilterProp="label"
              placeholder="Select a customer"
              onChange={() => setPreview(null)}
              options={customers.map((c) => ({
                value: c.id,
                label: `${c.clientName} (${c.accountNumber})`,
              }))}
            />
          </Form.Item>
        )}

        {preview && (
          <>
            <Divider style={{ borderColor: '#eaecf0' }}>Preview</Divider>
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Customer">{preview.clientName}</Descriptions.Item>
              <Descriptions.Item label="Plan">{preview.planName}</Descriptions.Item>
              <Descriptions.Item label="Seats">{preview.seatCount}</Descriptions.Item>
              <Descriptions.Item label="Unit price">{money(preview.unitPrice, preview.currency)}</Descriptions.Item>
              <Descriptions.Item label="Total">
                <strong>{money(preview.total, preview.currency)}</strong>
              </Descriptions.Item>
            </Descriptions>
          </>
        )}

        <Divider style={{ borderColor: '#eaecf0' }} />
        <Form.Item name="issueDate" label="Issue date" rules={[{ required: true }]}>
          <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
        </Form.Item>
        <Form.Item name="dueDate" label="Due date" rules={[{ required: true }]}>
          <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CreateInvoiceModal;
